import type { TransportConfig } from "@eventmsg/core";

/**
 * Web Bluetooth service configuration
 */
export interface WebBLEServiceConfig {
  /**
   * Service UUID (e.g., Nordic UART Service)
   */
  uuid: string;

  /**
   * TX characteristic UUID (device → browser, notification)
   */
  txCharacteristic: string;

  /**
   * RX characteristic UUID (browser → device, write)
   */
  rxCharacteristic: string;
}

/**
 * Connection behavior configuration
 */
export interface WebBLEConnectionConfig {
  /**
   * Connection timeout in milliseconds
   * @default 10_000
   */
  timeout?: number;

  /**
   * Preferred MTU size in bytes
   * @default auto-negotiate
   */
  mtu?: number;

  /**
   * Whether to cache device for reconnection
   * @default true
   */
  persistDevice?: boolean;

  /**
   * Number of automatic reconnection attempts
   * @default 3
   */
  reconnectAttempts?: number;

  /**
   * Delay between reconnection attempts in milliseconds
   * @default 1_000
   */
  reconnectDelay?: number;
}

/**
 * Configuration for WebBLE transport extending base TransportConfig
 */
export interface WebBLETransportConfig extends TransportConfig {
  /**
   * BLE service configuration
   */
  service: WebBLEServiceConfig;

  /**
   * Optional device filtering for requestDevice()
   */
  filters?: BluetoothLEScanFilter[];

  /**
   * Connection behavior configuration
   */
  connection?: WebBLEConnectionConfig;
}

/**
 * Nordic UART Service preset configuration
 * Standard service used by most ESP32 BLE examples
 */
export const NORDIC_UART_SERVICE: WebBLEServiceConfig = {
  uuid: "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
  txCharacteristic: "6e400003-b5a3-f393-e0a9-e50e24dcca9e", // notify
  rxCharacteristic: "6e400002-b5a3-f393-e0a9-e50e24dcca9e", // write
};

/**
 * Create a WebBLE transport config with Nordic UART defaults
 */
export function createNordicUARTConfig(
  localAddress: number,
  groupAddress: number,
  overrides?: Partial<WebBLETransportConfig>
): WebBLETransportConfig {
  return {
    localAddress,
    groupAddress,
    service: NORDIC_UART_SERVICE,
    connection: {
      timeout: 10_000,
      persistDevice: true,
      reconnectAttempts: 3,
      reconnectDelay: 1000,
      mtu: 480,
    },
    ...overrides,
  };
}
